import { useState, useEffect, useCallback, useRef } from 'react';
import { PaymentSummaryDto, CreateSalesOrderPaymentDto, GenerateInstallmentsDto } from '../api';
import { salesService } from '../services/sales.service';
import { SalesOrder } from '../types/sales.types';
import { PAYMENT_METHODS } from '../utils/constants/paymentMethods';
import { extractErrorMessage } from '../utils/errorHelpers';

interface UsePaymentModalReturn {
  // State
  isLoading: boolean;
  isLoadingSummary: boolean;
  error: string | null;
  success: string | null;
  paymentSummary: PaymentSummaryDto | null;
  remainingBalance: number;

  // Actions
  loadPaymentSummary: () => Promise<void>;
  /** @returns true when the payment was persisted successfully */
  processPayment: (
    paymentData: PaymentFormData,
    options?: { notify?: boolean },
  ) => Promise<boolean>;
  reportError: (message: string) => void;
  clearError: () => void;
  clearSuccess: () => void;
}

interface PaymentFormData {
  paymentAmount: number;
  paymentMethod: string;
  paymentType: 'FULL' | 'PARTIAL';
  changeGiven: number;
  paymentNotes: string;
  cashReceived: number;
  bankReference: string;
  qrTransactionId: string;
  /** Commercial payment date (ISO). Sent only when user can modify sale date. */
  paymentDate?: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface InstallmentData {
  enabled: boolean;
  numberOfInstallments: number;
  frequency: GenerateInstallmentsDto.frequency;
  firstDueDate: string;
  installmentAmount: number;
}

export const usePaymentModal = (
  order: SalesOrder | null,
  onPaymentProcessed?: () => void,
): UsePaymentModalReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const paymentInFlightRef = useRef(false);
  const [isLoadingSummary, setIsLoadingSummary] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [paymentSummary, setPaymentSummary] = useState<PaymentSummaryDto | null>(null);
  const [remainingBalance, setRemainingBalance] = useState(0);

  const loadPaymentSummary = useCallback(async () => {
    if (!order) return;

    setIsLoadingSummary(true);
    try {
      const summary = await salesService.getPaymentSummary(order.id);
      // Round all monetary values to avoid floating point precision issues
      const roundedSummary = {
        ...summary,
        totalPaid: Math.round(summary.totalPaid * 100) / 100,
        remainingBalance: Math.round(summary.remainingBalance * 100) / 100,
        orderTotal: Math.round(summary.orderTotal * 100) / 100,
        completionPercentage: Math.round(summary.completionPercentage * 100) / 100,
      };
      setPaymentSummary(roundedSummary);
      setRemainingBalance(roundedSummary.remainingBalance);
    } catch (err: any) {
      console.error('Error loading payment summary:', err);
      setError('Error al cargar el resumen de pagos');
    } finally {
      setIsLoadingSummary(false);
    }
  }, [order]);

  const processPayment = useCallback(
    async (paymentData: PaymentFormData, options?: { notify?: boolean }): Promise<boolean> => {
      if (!order) return false;

      // Reject concurrent callers (double-click); sequential batch calls still await one-by-one
      if (paymentInFlightRef.current) {
        return false;
      }
      paymentInFlightRef.current = true;
      setIsLoading(true);
      setError(null);
      setSuccess(null);

      try {
        // Validate payment
        if (paymentData.paymentAmount <= 0) {
          throw new Error('El monto del pago debe ser mayor a 0');
        }

        if (paymentData.paymentAmount > Math.round(remainingBalance * 100) / 100) {
          throw new Error('El monto del pago no puede ser mayor al saldo pendiente');
        }

        if (
          paymentData.paymentMethod === PAYMENT_METHODS.CASH &&
          Math.round(paymentData.cashReceived * 100) / 100 <
            Math.round(paymentData.paymentAmount * 100) / 100
        ) {
          throw new Error('El monto recibido en efectivo debe ser mayor o igual al monto del pago');
        }

        // Prepare payment request
        const trimmedNotes = paymentData.paymentNotes?.trim();
        const paymentRequest = {
          salesOrderId: order.id,
          paymentAmount: paymentData.paymentAmount,
          paymentMethod: paymentData.paymentMethod,
          paymentType: CreateSalesOrderPaymentDto.paymentType.ORDER,
          changeGiven: paymentData.changeGiven,
          ...(trimmedNotes != null && trimmedNotes !== '' ? { paymentNotes: trimmedNotes } : {}),
          bankReference: paymentData.bankReference || undefined,
          qrTransactionId: paymentData.qrTransactionId || undefined,
          ...(paymentData.paymentDate != null && paymentData.paymentDate !== ''
            ? { paymentDate: paymentData.paymentDate }
            : {}),
        };

        await salesService.processPayment(paymentRequest);

        setSuccess('Pago registrado con éxito.');
        await loadPaymentSummary();
        if (options?.notify !== false) {
          onPaymentProcessed?.();
        }
        return true;
      } catch (err: unknown) {
        console.error('Error processing payment:', err);
        setError(extractErrorMessage(err, 'Error al procesar el pago'));
        // Refresh summary so UI reflects balance if another cashier already paid
        await loadPaymentSummary();
        return false;
      } finally {
        paymentInFlightRef.current = false;
        setIsLoading(false);
      }
    },
    [order, remainingBalance, loadPaymentSummary, onPaymentProcessed],
  );

  const reportError = useCallback((message: string) => {
    setError(message);
    setSuccess(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearSuccess = useCallback(() => {
    setSuccess(null);
  }, []);

  // Load payment summary when order changes
  useEffect(() => {
    if (order) {
      loadPaymentSummary();
    }
  }, [order, loadPaymentSummary]);

  return {
    isLoading,
    isLoadingSummary,
    error,
    success,
    paymentSummary,
    remainingBalance,
    loadPaymentSummary,
    processPayment,
    reportError,
    clearError,
    clearSuccess,
  };
};
